/**
 * Tax Deductions Page
 * Shows all tax-deductible items grouped by deduction class
 * (form-agnostic — Schedule C / Form 1120 line numbers come from the
 * deduction class's mapping in @bizsolutions/shared/tax/deduction-form-lines).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Receipt, TrendingDown } from 'lucide-react';
import { DEDUCTION_FORM_LINES, type DeductionClass } from '@bizsolutions/shared';

interface DeductionItem {
  sourceType: string;
  sourceId: string;
  description: string;
  amount: number;
  itemDate: string;
  taxDeductionClass: string;
  vendor: string | null;
  categoryName: string | null;
  businessProfileName: string | null;
}

interface BusinessProfile {
  id: string;
  name: string;
}

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

function classLabel(cls: string): string {
  return DEDUCTION_FORM_LINES[cls as DeductionClass]?.label || cls;
}

function classFormLines(cls: string): string {
  const m = DEDUCTION_FORM_LINES[cls as DeductionClass];
  if (!m) return '';
  const sc = m.schedule_c ? `Sched C ${m.schedule_c}` : 'Sched C —';
  const f1120 = m.form_1120 === 'OTHER' ? '1120 Other' : m.form_1120 ? `1120 ${m.form_1120}` : '1120 —';
  return `${sc} · ${f1120}`;
}

export function DeductionsPage() {
  const [taxYear, setTaxYear] = useState(currentYear);
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [businessProfileId, setBusinessProfileId] = useState<string>('all');

  const { data: profilesData } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => api.getBusinessProfiles(),
  });
  const profiles = (profilesData?.data || []) as BusinessProfile[];

  const { data, isLoading } = useQuery({
    queryKey: ['tax-deductions', taxYear, selectedClass, businessProfileId],
    queryFn: async () => {
      const result = await api.getTaxDeductions({
        taxYear,
        taxDeductionClass: selectedClass !== 'all' ? selectedClass : undefined,
        businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      });
      return result.data as { items: DeductionItem[] };
    },
  });

  const allItems = data?.items || [];
  const totalAmount = allItems.reduce((sum, item) => sum + item.amount, 0);

  // Group by deduction class for the sidebar summary
  const classTotals = new Map<string, { count: number; total: number }>();
  for (const item of allItems) {
    const existing = classTotals.get(item.taxDeductionClass) || { count: 0, total: 0 };
    existing.count++;
    existing.total += item.amount;
    classTotals.set(item.taxDeductionClass, existing);
  }

  const sortedClasses = Array.from(classTotals.entries())
    .sort((a, b) => b[1].total - a[1].total);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tax Deductions</h1>
          <p className="text-muted-foreground">All tax-deductible items grouped by deduction class</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={businessProfileId} onValueChange={setBusinessProfileId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Businesses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Businesses</SelectItem>
              {profiles.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={taxYear.toString()} onValueChange={(v) => setTaxYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(y => (
                <SelectItem key={y} value={y.toString()}>TY {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Deductions</CardTitle>
            <TrendingDown className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{formatCurrency(totalAmount)}</div>
            <p className="text-xs text-muted-foreground">{allItems.length} deductible items</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Deduction Classes</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{classTotals.size}</div>
            <p className="text-xs text-muted-foreground">Distinct classes used this year</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg per Item</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(allItems.length > 0 ? totalAmount / allItems.length : 0)}
            </div>
            <p className="text-xs text-muted-foreground">Across all deductible items</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Class Filter Sidebar */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Deduction Classes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <button
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                selectedClass === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
              onClick={() => setSelectedClass('all')}
            >
              All Classes ({allItems.length})
            </button>
            {sortedClasses.map(([cls, d]) => (
              <button
                key={cls}
                className={`w-full text-left px-3 py-2 rounded text-sm transition-colors flex justify-between ${
                  selectedClass === cls ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                }`}
                onClick={() => setSelectedClass(cls)}
                title={classFormLines(cls)}
              >
                <span className="truncate mr-2">{classLabel(cls)}</span>
                <span className="shrink-0">{formatCurrency(d.total)}</span>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Deduction Items */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>
              {selectedClass === 'all' ? 'All Deductible Items' : classLabel(selectedClass)}
              <span className="text-muted-foreground font-normal ml-2">
                ({allItems.length} items &middot; {formatCurrency(totalAmount)})
              </span>
            </CardTitle>
            {selectedClass !== 'all' && (
              <p className="text-xs text-muted-foreground">
                Form lines: {classFormLines(selectedClass)}
              </p>
            )}
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : allItems.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No deductible items found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {allItems.map((item) => (
                  <div
                    key={`${item.sourceType}-${item.sourceId}`}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Receipt className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{item.description}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                          {item.vendor && <span>{item.vendor}</span>}
                          <span>{formatDate(item.itemDate)}</span>
                          {item.businessProfileName && (
                            <span>&middot; {item.businessProfileName}</span>
                          )}
                          {item.categoryName && (
                            <span>&middot; {item.categoryName}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant="secondary" title={classFormLines(item.taxDeductionClass)}>
                        {classLabel(item.taxDeductionClass)}
                      </Badge>
                      <span className="font-medium text-emerald-600 w-24 text-right">
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
